import classnames from 'classnames'
import ApiClient from 'clients/api_client'
import Button from 'components/button'
import Dropdown from 'components/dropdown'
import DropdownMenu from 'components/dropdown_menu'
import Group from 'entities/group'
import Pagination from 'entities/pagination'
import { debounce, isEmpty, trim } from 'lodash'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { propTypeInstantiableTo } from 'supports/custom_prop_types'
import Paginate from '../pagination'
import styles from './group_dropdown.module.scss'
import SwitchableGroups from './switchable_groups'

export default class GroupDropdown extends Component {
  constructor(props) {
    super(props)
    this.state = {
      focusIndex: 0,
      groups: [],
      pagination: Pagination.initializeWithPage(),
      keyword: '',
      isOpened: false
    }

    this.fetchGroupsAsync = debounce(this.fetchGroups, 200, { maxWait: 1000 })
    this.fetchGroupsAsync()
  }

  componentDidMount() {
    document.addEventListener('keydown', e => this.handleKeyDown(e))
  }

  componentDidUpdate(_prevProps, prevState) {
    const { isOpened } = this.state

    if (!prevState.isOpened && isOpened) {
      this.focusMenuItem()
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', e => this.handleKeyDown(e))
  }

  focusMenuItem(focusIndex) {
    this.setState(
      {
        focusIndex
      },
      () => this.menuItems.childNodes[focusIndex] && this.menuItems.childNodes[focusIndex].focus()
    )
  }

  handleKeyDown(e) {
    const { key } = e
    const { groups, focusIndex, isOpened } = this.state
    const hasInputFocus = document.activeElement === this.input

    if (!isOpened || isEmpty(groups) || hasInputFocus) {
      return false
    }

    e.preventDefault()

    const groupLength = groups.length - 1

    switch (key) {
      case 'Enter':
        return this.menuItems.childNodes[focusIndex] && this.menuItems.childNodes[focusIndex].click()
      case 'Down':
      case 'ArrowDown': {
        const newFocusIndex = focusIndex === groupLength ? 0 : focusIndex + 1
        return this.focusMenuItem(newFocusIndex)
      }
      case 'Up':
      case 'ArrowUp': {
        const newFocusIndex = focusIndex === 0 ? groupLength : focusIndex - 1
        return this.focusMenuItem(newFocusIndex)
      }
      default:
        return false
    }
  }

  handleMouseEnter(e) {
    const { groups, isOpened } = this.state

    if (!isOpened || isEmpty(groups) || !this.menuItems) {
      return false
    }

    const { currentTarget } = e
    const newFocusedIndex = parseInt(currentTarget.dataset.index, 10)

    return this.setState({
      focusIndex: newFocusedIndex
    })
  }

  handleNameChange(e) {
    e.preventDefault()
    const { value } = e.target
    this.setState(
      {
        keyword: trim(value)
      },
      () => this.fetchGroupsAsync()
    )
  }

  handlePageClick(page = 1) {
    this.fetchGroupsAsync(page)
  }

  fetchGroups(page = 1) {
    const { keyword } = this.state

    const url = Routes.searchGroupsPath({ page })
    return ApiClient.post(url, { keyword }).then(({ data, headers }) => {
      this.setState({
        groups: data.map(g => new Group(g)),
        pagination: Pagination.fromJsonLowerCased(headers),
        focusIndex: 0
      })
    })
  }

  toggleOpenState() {
    const { isOpened } = this.state

    this.setState({
      isOpened: !isOpened
    })
  }

  render() {
    const { group, name } = this.props
    const { groups, pagination, isOpened } = this.state
    const { totalPages } = pagination
    const buttonClassName = classnames(styles.toggle, {
      'is-active': isOpened
    })

    return (
      <Dropdown className={styles.dropdown} show={isOpened}>
        <Button className={buttonClassName} none onClick={() => this.toggleOpenState()}>
          {`${name} ${group.name}`}
          <div className={styles.caret} />
        </Button>
        <DropdownMenu className={styles.menu} show={isOpened} onClick={() => this.toggleOpenState()}>
          <div className={styles.header}>
            <div className={styles.headerTitle}>{I18n.t('components.app_header.group_dropdown.title')}</div>
          </div>
          <div className={styles.keywordFilter}>
            <input
              name="keyword"
              type="text"
              className="form-control"
              placeholder={I18n.t('components.app_header.group_dropdown.keyword.placeholder')}
              onKeyUp={e => this.handleNameChange(e)}
              onCompositionEnd={e => this.handleNameChange(e)}
              ref={ref => {
                this.input = ref
              }}
            />
          </div>
          <div
            ref={ref => {
              this.menuItems = ref
            }}
          >
            <SwitchableGroups groups={groups} selected={group} onMouseEnter={e => this.handleMouseEnter(e)} />
          </div>
          {totalPages > 1 ? (
            <div className={styles.paginator}>
              <Paginate pagination={pagination} onPaginationClick={page => this.handlePageClick(page)} />
            </div>
          ) : null}
        </DropdownMenu>
      </Dropdown>
    )
  }
}

GroupDropdown.propTypes = {
  group: propTypeInstantiableTo(Group).isRequired,
  name: PropTypes.string.isRequired
}
